import React, { useState, useEffect } from "react";
import "./articles.css";
import Favo from "../../media/fav.jpeg"; 
import { removeFavorite, createReview, getAIReviewThemeAndPresentation, isAiActivated, fetchFavorites } from "../../HTTP/articles" 
import * as helpers from "../../Helper/utils";
import userApi from "../../HTTP/user"
import { languages } from "../../global/countriesAndSources";
import { GenerateMultiFactChecking } from "./generateFactCheck";
import AddIcon from '@mui/icons-material/Add';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import DeleteIcon from '@mui/icons-material/Delete';
import SelectAllIcon from '@mui/icons-material/SelectAll';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { fetchUserTokens } from "../../HTTP/subscription";

const ArticlesFav = () => {
  // États principaux
  const [user, setUser] = useState(null);
  const [favorites, setFavorites] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // États de l'interface
  const [showPopup, setShowPopup] = useState(false);
  const [showAIOptions, setShowAIOptions] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState(sessionStorage.getItem('currentlanguage') || "fr");
  const [selectedTone, setSelectedTone] = useState("synthétique");
  const [selection, setSelection] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [aiGenerated, setAiGenerated] = useState(false);
  const [aiActivated, setAiActivated] = useState(false);
  const [showFactCheckingPopup, setShowFactCheckingPopup] = useState(false);
  const [expandedReviews, setExpandedReviews] = useState({});
  const [userTokens, setUserTokens] = useState(1);
  
  const toneOptions = [
    "synthétique",
    "journalistique", 
    "pédagogique",
    "académique",
    "diplomatique",
    "narratif",
    "littéraire",
    "humoristique", 
    "polémique",
    "engagé/militant"
  ];

  // Récupération des informations utilisateur
  useEffect(() => {
    const userData = localStorage.getItem("user");
    
    if (!userData) {
      window.location.href = '/';
      return;
    }
    
    try {
      const parsedUser = JSON.parse(userData);
      setUser(parsedUser);
    } catch (error) {
      console.error('Erreur lors du parsing des données utilisateur:', error);
      window.location.href = '/';
    }
  }, []);

  // Chargement des données depuis l'API
  useEffect(() => {
    if (!user?.id) return;

    const fetchData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // Charger les favoris, reviews et statut AI en parallèle
        const [userTokens, favoritesData, reviewsData, aiStatus] = await Promise.allSettled([
          fetchUserTokens(user.id),
          fetchFavorites(),
          userApi.getUserReviews(user.id),
          isAiActivated()
        ]);

        console.log(userTokens, favoritesData, reviewsData, aiStatus);
        // Traitement des tokens utilisateur
        if (userTokens.status === 'fulfilled') {
          setUserTokens(userTokens.value.tokens);
        } else {
          console.error('Erreur lors du chargement des tokens utilisateur:', userTokens.reason);
          setUserTokens(0); // Valeur par défaut en cas d'erreur
        }

        // Traitement des favoris
        if (favoritesData.status === 'fulfilled') {
          setFavorites(favoritesData.value || []);
        } else {
          console.error('Erreur lors du chargement des favoris:', favoritesData.reason);
        }

        // Traitement des reviews
        if (reviewsData.status === 'fulfilled') {
          setReviews(reviewsData.value?.reviews || []);
        } else {
          console.error('Erreur lors du chargement des reviews:', reviewsData.reason);
        }

        // Traitement du statut AI
        if (aiStatus.status === 'fulfilled' && aiStatus.value !== undefined) {
          setAiActivated(aiStatus.value.value === "1");
        } else {
          console.error('Erreur lors du chargement du statut AI:', aiStatus.reason);
        }

      } catch (error) {
        console.error('Erreur générale lors du chargement des données:', error);
        setError('Erreur lors du chargement des données');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user?.id]);

  // Gestion de la sélection
  const handleSelectionChange = (favoriteId, isSelected) => {
    setSelection(prev => {
      if (isSelected) {
        const favorite = favorites.find(fav => fav.id === favoriteId);
        return favorite ? [...prev, favorite] : prev;
      } else {
        return prev.filter(fav => fav.id !== favoriteId);
      }
    });
  };

  const handleSelectAll = () => {
    if (selection.length === filteredFavorites.length) {
      // Tout désélectionner
      setSelection([]);
    } else {
      // Tout sélectionner
      setSelection([...filteredFavorites]);
    }
  };

  // Suppression de favoris
  const handleRemoveMultipleFavorites = async () => {
    if (selection.length === 0) {
      alert("Veuillez sélectionner au moins un article avant de supprimer.");
      return;
    }

    setLoading(true);
    const errors = [];

    try {
      // Supprimer chaque favori sélectionné
      await Promise.allSettled(
        selection.map(async (favorite) => {
          try {
            await removeFavorite(favorite);
            return favorite.id;
          } catch (error) {
            errors.push(`Erreur lors de la suppression de "${favorite.title}"`);
            throw error;
          }
        })
      );

      // Mettre à jour la liste des favoris
      const removedIds = selection.map(fav => fav.id);
      setFavorites(prev => prev.filter(fav => !removedIds.includes(fav.id)));
      setSelection([]);
      
      if (errors.length === 0) {
        alert('Favoris supprimés avec succès');
      } else {
        alert(`Suppression terminée avec ${errors.length} erreur(s)`);
      }

    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      alert('Erreur lors de la suppression des favoris');
    } finally {
      setLoading(false);
    }
  };

  // Filtrage des favoris
  const filteredFavorites = favorites.filter(fav => 
    fav.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    fav.link?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    fav.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    fav.country?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Gestion des popups
  const togglePopup = () => {
    setShowPopup(!showPopup);
    setAiGenerated(false);
    setShowAIOptions(false);
  };

  const toggleAIOptions = () => {
    setShowAIOptions(!showAIOptions);
  };

  const toggleReviewsExpand = (index) => {
    setExpandedReviews(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  // Création de review
  const handleValidate = () => {
    if (document.getElementById('fact_check')?.checked) {
      if (selection.length === 0) {
        alert("Veuillez sélectionner au moins un article avant de générer un fact-checking.");
        return;
      }
      setShowFactCheckingPopup(true);
    } else {
      addReview();
    }
  };

  const addReview = async () => {
    const theme = document.getElementById('review_theme').value;
    const presentation = document.getElementById('review_presentation').value;
    
    try {
      const res = await createReview(
        theme, 
        presentation, 
        selection, 
        !aiGenerated ? document.getElementById('publishable_review')?.checked : 0
      );
      
      setShowPopup(false);
      alert('Review créée avec succès');
      window.location.href = `https://static.zemus.info/?id=${res.newReview}&redirect=favourites&readerid=${user.id}`;
    } catch (error) {
      console.error('Erreur lors de la création de la review:', error);
      alert('Erreur lors de la création de la review');
    }
  };

  // Génération avec IA
  const generateWithAI = async () => {
    if (selection.length === 0) {
      alert("Veuillez sélectionner au moins un article avant de générer du contenu.");
      return;
    }

    setIsLoading(true);
    
    try {
      const articleLinks = selection.map(item => helpers.htmlDecode(item.link));
      
      const result = await getAIReviewThemeAndPresentation(
        selectedLanguage,
        selectedTone,
        articleLinks,
        user.id, 
      );
      
      if (result?.theme) {
        document.getElementById('review_theme').value = result.theme;
      }
      
      if (result?.presentation) {
        const formattedPresentation = result.presentation.map((item, index) => {
          return `${index + 1} -\n\n${item}`;
        });
        document.getElementById('review_presentation').value = formattedPresentation.join('\n\n');
      }
      
      setShowAIOptions(false);
      setAiGenerated(true);
      
    } catch (error) {
      console.error("Erreur lors de la génération avec l'IA:", error);
      alert("Une erreur s'est produite lors de la génération avec l'IA.");
    } finally {
      setIsLoading(false);
    }
  };

  // Navigation vers d'autres pages
  const addToExistingReview = () => { 
    const jsonString = JSON.stringify(selection);
    const encodedString = encodeURIComponent(jsonString);
    window.location.href = `/addtoreview?data=${encodedString}`;
  };

  const addToExistingFriendReview = () => { 
    const jsonString = JSON.stringify(selection);
    const encodedString = encodeURIComponent(jsonString);
    window.location.href = `/addtofriendreview?data=${encodedString}`;
  };

  // Rendu des reviews
  const renderReviews = (faviLink, index) => {
    const faviReviews = reviews.filter(review => 
      review.articles && review.articles.some(article => 
        helpers.htmlDecode(article.link) === helpers.htmlDecode(faviLink)
      )
    );
    
    if (faviReviews.length === 0) return null;
    
    const isExpanded = expandedReviews[index] || false;
    const displayedReviews = isExpanded ? faviReviews : faviReviews.slice(0, 2);
    
    return (
      <div className="review mb-4">
        <h4 className="text-black text-xs lg:text-base mb-1">
          Présent dans {faviReviews.length > 1 ? "ces" : "cette"} revue{faviReviews.length > 1 ? "s" : ""} de presse :
        </h4>
        <ul>
          {displayedReviews.map((review, idx) => (
            <li key={idx} className="text-black text-xs lg:text-base">
              <a 
                href={`https://static.zemus.info/?id=${review.id}&redirect=favourites&readerid=${user.id}`} 
                target="_blank" 
                rel="noreferrer"
              >
                - {helpers.htmlDecode(review.theme)}
              </a>
            </li>
          ))}
        </ul>
        
        {faviReviews.length > 2 && (
          <button 
            onClick={() => toggleReviewsExpand(index)}
            className="text-cyan-600 text-sm mt-1 flex items-center hover:text-cyan-800"
          >
            {isExpanded ? "Voir moins" : `Voir ${faviReviews.length - 2} de plus`}
            <ExpandMoreIcon 
              fontSize="small" 
              className={`ml-1 transform transition-transform ${isExpanded ? 'rotate-180' : ''}`}
            />
          </button>
        )}
      </div>
    );
  };

  // Affichage du chargement
  if (loading) {
    return (
      <section className="articlesfav">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#009AAD]"></div>
          <p className="ml-4 text-[#009AAD]">Chargement des favoris...</p>
        </div>
      </section>
    );
  }

  // Affichage d'erreur
  if (error) {
    return (
      <section className="articlesfav">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <p className="text-red-500 mb-4">{error}</p>
            <button 
              onClick={() => window.location.reload()} 
              className="bg-[#009AAD] text-white px-4 py-2 rounded"
            >
              Réessayer
            </button>
          </div>
        </div>
      </section>
    );
  }

  const isOneSelected = selection.length > 0;

  return (
    <>
      {showPopup && <div className="blur-background"></div>}
      {showPopup && (
        <div className="popup"> 
          <div style={{ display: "flex" }} className="headtitle">
            <h2>Créer une revue de Presse</h2>
            <span onClick={togglePopup} className="material-symbols-outlined">
              close
            </span>
          </div>
          <div>
            <div className="Message">
              <input
                title="Write Message"
                name="theme"
                placeholder="Thème..."
                className="MsgInput"
                type="text"
                maxLength="255"
                id="review_theme"
              /> 
            </div>
            <div className="Message">
              <textarea
                name="presentation"
                placeholder="Description..."
                className="MsgInput"
                rows={4}
                id="review_presentation"
              /> 
            </div>
            
            {!aiGenerated && (
              <div className="Message">
                <input
                  name="publishable_review"
                  type="checkbox"
                  maxLength="255"
                  id="publishable_review"
                /> 
                <label htmlFor="publishable_review" className="text-white ml-2">
                  Autoriser mes amis à publier leurs propres favoris dans cette revue de presse
                </label>
              </div>
            )}

            {aiGenerated && (
              <div className="Message">
                <input
                  name="fact_check"
                  type="checkbox"
                  id="fact_check"
                /> 
                <label htmlFor="fact_check" className="text-white ml-2">
                  Générer un fact-checking pour cette revue de presse
                </label>
              </div>
            )}
            
            <div className="Message">
              {aiActivated && !aiGenerated && userTokens > 0 && (
                <button 
                  className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-5 rounded mr-2"
                  onClick={toggleAIOptions}
                  disabled={isLoading}
                >
                  {showAIOptions ? "Masquer les options d'IA" : "Remplir grâce à l'IA"}
                </button>
              )}
              
              <button 
                className="bg-yellow-500 hover:bg-yellow-700 text-white font-bold py-2 px-5 rounded"
                onClick={handleValidate}
                disabled={isLoading}
              >
                Valider
              </button>
            </div>
            
            {aiActivated && showAIOptions && (
              <div className="ai-options p-3 border border-gray-300 rounded mt-3 mb-3">
                <div className="flex flex-col sm:flex-row gap-3 mb-3">
                  <div className="flex-1">
                    <label htmlFor="language-select" className="block text-white mb-1">
                      Langue:
                    </label>
                    <select
                      id="language-select"
                      className="w-full p-2 rounded"
                      value={selectedLanguage}
                      onChange={(e) => setSelectedLanguage(e.target.value)}
                      disabled={isLoading}
                    >
                      {languages.map((lang) => (
                        <option key={lang.language_code} value={lang.language_code}>
                          {lang.language_name}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="flex-1">
                    <label htmlFor="tone-select" className="block text-white mb-1">
                      Ton journalistique:
                    </label>
                    <select
                      id="tone-select"
                      className="w-full p-2 rounded"
                      value={selectedTone}
                      onChange={(e) => setSelectedTone(e.target.value)}
                      disabled={isLoading}
                    >
                      {toneOptions.map((tone) => (
                        <option key={tone} value={tone}>
                          {tone.charAt(0).toUpperCase() + tone.slice(1)}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                
                <button
                  className={`text-white font-bold py-2 px-5 rounded w-full flex justify-center items-center ${
                    isLoading ? 'bg-gray-500' : 'bg-green-500 hover:bg-green-700'
                  }`}
                  onClick={generateWithAI}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <div className="flex items-center">
                      <div className="animate-spin h-5 w-5 mr-2 border-t-2 border-b-2 border-white rounded-full"></div>
                      Génération en cours...
                    </div>
                  ) : (
                    'Générer'
                  )}
                </button>
              </div>
            )}
            
            {isLoading && (
              <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center">
                <div className="bg-white p-5 rounded-lg shadow-lg flex flex-col items-center">
                  <div className="w-12 h-12 border-4 border-t-4 border-gray-200 rounded-full animate-spin mb-3" style={{ borderTopColor: '#3B82F6' }}></div>
                  <p className="text-gray-700 font-medium">Génération en cours...</p>
                  <p className="text-gray-500 text-sm mt-1">Cela peut prendre un moment</p>
                </div>
              </div>
            )}
            
            <div className="Message">
              {selection.map((fav, i) => (
                <img
                  key={i}
                  className="item-photofav-add"
                  src={helpers.htmlDecode(fav.image || process.env.PUBLIC_URL + "/news_icon2.jpg")}
                  alt="article"  
                />
              ))}
            </div>
          </div>
        </div>
      )}

      {showFactCheckingPopup && (
        <GenerateMultiFactChecking 
          articleLinks={selection.map(item => helpers.htmlDecode(item.link))}
          setShowPopUpFactChecking={setShowFactCheckingPopup}
          addReview={addReview}
        />
      )}

      <section className="articlesfav">
        <div className="search-bar">
          <input
            type="text"
            placeholder="Rechercher parmi les favoris..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>

        <div className="flex flex-row justify-around lg:justify-center mt-14 mb-9 lg:space-x-6">
          <button 
            onClick={togglePopup} 
            className="-mt-12 border-none py-3 px-3.5 lg:py-2.5 lg:px-4 text-white bg-[#009AAD] text-sm rounded-full font-bold font-sans flex items-center justify-center h-12 w-12 lg:w-auto lg:h-auto"
          >
            <span className="hidden lg:inline">Créer une revue de presse</span>
            <span className="lg:hidden">
              <AddIcon fontSize="medium" />
            </span>
          </button>
          
          <button 
            onClick={addToExistingReview} 
            disabled={!isOneSelected} 
            className={`-mt-12 border-none py-3 px-3.5 lg:py-2.5 lg:px-4 text-white bg-[#009AAD] text-sm rounded-full font-bold font-sans flex items-center justify-center h-12 w-12 lg:w-auto lg:h-auto ${
              !isOneSelected ? 'opacity-50' : ''
            }`}
          >
            <span className="hidden lg:inline">Ajouter à une revue de presse</span>
            <span className="lg:hidden">
              <PlaylistAddIcon fontSize="medium" />
            </span>
          </button>
          
          <button 
            onClick={addToExistingFriendReview} 
            disabled={!isOneSelected} 
            className={`-mt-12 border-none py-3 px-3.5 lg:py-2.5 lg:px-4 text-white bg-[#009AAD] text-sm rounded-full font-bold font-sans flex items-center justify-center h-12 w-12 lg:w-auto lg:h-auto ${
              !isOneSelected ? 'opacity-50' : ''
            }`}
          >
            <span className="hidden lg:inline">Ajouter à une revue de presse d'un ami</span>
            <span className="lg:hidden">
              <PersonAddIcon fontSize="medium" />
            </span>
          </button>
          
          <button 
            onClick={handleRemoveMultipleFavorites} 
            disabled={!isOneSelected} 
            className={`-mt-12 border-none py-3 px-3.5 lg:py-2.5 lg:px-4 text-white bg-[#009AAD] text-sm rounded-full font-bold font-sans flex items-center justify-center h-12 w-12 lg:w-auto lg:h-auto ${
              !isOneSelected ? 'opacity-50' : ''
            }`}
          >
            <span className="hidden lg:inline">Supprimer</span>
            <span className="lg:hidden">
              <DeleteIcon fontSize="medium" />
            </span>
          </button>
          
          <button 
            onClick={handleSelectAll} 
            className="-mt-12 border-none py-3 px-3.5 lg:py-2.5 lg:px-4 text-white bg-[#009AAD] text-sm rounded-full font-bold font-sans flex items-center justify-center h-12 w-12 lg:w-auto lg:h-auto"
          >
            <span className="hidden lg:inline">
              {selection.length === filteredFavorites.length ? 'Tout décocher' : 'Tout cocher'}
            </span>
            <span className="lg:hidden">
              <SelectAllIcon fontSize="medium" />
            </span>
          </button>
        </div>

        <div className="allArticlesDiv h-100 overflow-y-auto" style={{ maxHeight: "calc(100vh - 250px)" }}>
          {filteredFavorites.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500">Aucun favori trouvé</p>
            </div>
          ) : (
            filteredFavorites.map((fav, i) => (
              <div className="featuresfav" key={fav.id || i}>
                <div className="itemfav rounded-lg shadow m-2">
                  <div className="item-photowrapper">
                    <img
                      className="item-photofav"
                      src={helpers.htmlDecode(fav.image || process.env.PUBLIC_URL + "/news_icon2.jpg")}
                      alt="article"
                      onClick={() => window.open(helpers.htmlDecode(fav.link), "_blank")} 
                    />
                  </div>
                  <div className="arttfav">
                    <div className="item-headerfav"></div>
                    <a href={helpers.htmlDecode(fav.link)} target="_blank" rel="noreferrer" style={{ textDecoration: "inherit", color: "inherit" }}>
                      <h3 className="item-titlefav">
                        {helpers.htmlDecode(fav.title)}
                      </h3>
                      <p>
                        <strong className="text-cyan-600">
                          {helpers.getDomainName(fav.link) === "static.zemus.info" ? "Article - " : "Recherche - "}
                          Pays : {helpers.htmlDecode(fav.country)} | Source : {helpers.getDomainName(fav.link)} 
                        </strong>
                      </p>  
                      <p className="item-textfav">
                        {helpers.htmlDecode(fav.description)}
                      </p>
                    </a>
                    <div>
                      {renderReviews(fav.link, i)}
                    </div>
                  </div>

                  <div className="actiondiv">
                    <input 
                      type="checkbox" 
                      checked={selection.some(selected => selected.id === fav.id)}
                      onChange={(e) => handleSelectionChange(fav.id, e.target.checked)}
                    />
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </section>

      <style jsx>{`
        .loading-spinner-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 1rem;
        }
        
        .loading-spinner {
          border: 4px solid rgba(255, 255, 255, 0.3);
          border-radius: 50%;
          border-top: 4px solid #3B82F6;
          width: 2rem;
          height: 2rem;
          animation: spin 1s linear infinite;
          margin-bottom: 0.5rem;
        }
        
        .loading-text {
          color: white;
          font-size: 0.875rem;
        }
        
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .transform {
          transition-property: transform;
          transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
          transition-duration: 300ms;
        }
        
        .rotate-180 {
          transform: rotate(180deg);
        }
      `}</style>
    </>
  );
};

export default ArticlesFav;